import React, { useContext, useMemo, useState } from "react";
import styled from "styled-components";
import gql from "graphql-tag";
import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  useQuery,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { ControlContext } from "../../contexts/ControlContext";

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem("token");
  return {
    headers: {
      ...headers,
      authorization: token ? `${token}` : "",
    },
  };
});

const httpLink = createHttpLink({
  uri:
    (window.ENV?.REACT_APP_PROJECT_SERVER ||
      process.env.REACT_APP_PROJECT_SERVER) + "/graphql",
});

const project_client = new ApolloClient({
  cache: new InMemoryCache(),
  link: authLink.concat(httpLink),
});

const GET_PROJECTS = gql`
  query GetProjects {
    projectsForUser {
      _id
      name
      colour
    }
  }
`;

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgb(14, 18, 25);
  z-index: 100;
`;

const Main = styled.div`
  width: 420px;
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`;

const H1 = styled.h1`
  font-size: 2em;
  font-weight: 100;
  margin-bottom: 0.25em;
`;

const Sub = styled.div`
  color: #8a93a5;
  margin-bottom: 1.5em;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #1f222b;
  border-radius: 4px;
  padding: 1em;
  cursor: pointer;
  transition: 0.15s background-color;
  &:hover {
    background-color: #2a2e39;
  }
`;

const Swatch = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  flex-shrink: 0;
  background-color: ${({ colour }) => colour || "#c11938"};
`;

const Name = styled.div`
  color: #fff;
  font-weight: 600;
`;

const Message = styled.div`
  color: #8a93a5;
  padding: 1em 0;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1em;
`;

const Cancel = styled.div`
  color: #8a93a5;
  cursor: pointer;
  padding: 0.25em 0.5em;
  flex-shrink: 0;
  &:hover {
    color: #fff;
  }
`;

const Search = styled.input`
  background-color: #1f222b;
  border-radius: 4px;
  border: none;
  padding: 1em;
  margin-bottom: 1em;
  outline: none;
  color: #fff;
`;

export default function ProjectSelect() {
  const { projectId, setProjectId, closeProjectSelect } =
    useContext(ControlContext);
  const { loading, error, data } = useQuery(GET_PROJECTS, {
    fetchPolicy: "network-only",
    client: project_client,
  });
  const [filter, setFilter] = useState("");

  const choose = (id) => {
    setProjectId(id);
    closeProjectSelect();
  };

  const projects = useMemo(() => {
    const list = data?.projectsForUser || [];
    const q = filter.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [data, filter]);

  return (
    <Container>
      <Main>
        <Header>
          <div>
            <H1>Select a project</H1>
            <Sub>Choose the graphic project for the engine preview.</Sub>
          </div>
          {projectId && (
            <Cancel onClick={closeProjectSelect}>✕</Cancel>
          )}
        </Header>
        {loading && <Message>Loading projects…</Message>}
        {error && (
          <Message>Could not load projects. {error.message}</Message>
        )}
        {!loading && !error && (
          <>
            {(data?.projectsForUser || []).length > 6 && (
              <Search
                autoFocus
                placeholder="Search projects…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
            )}
            <List>
              {projects.map((p) => (
                <Item key={p._id} onClick={() => choose(p._id)}>
                  <Swatch colour={p.colour} />
                  <Name>{p.name || p._id}</Name>
                </Item>
              ))}
              {projects.length === 0 && (
                <Message>No projects found.</Message>
              )}
            </List>
          </>
        )}
      </Main>
    </Container>
  );
}
